import { PageHero, ProcessSection, Testimonials, FaqSection, CtaBand } from "@/components/site/sections";
import { Reveal, Overline, Cta } from "@/components/site/ui";
import { LeadForm } from "@/components/site/LeadForm";
import { SERVICES, KITCHEN_TYPES } from "@/data/content";
import { Check } from "lucide-react";

export default function ServiceDetail({ slug }) {
  const s = SERVICES.find((x) => x.slug === slug);
  if (!s) return null;

  return (
    <>
      <PageHero overline={`Service ${s.index}`} title={s.title} subtitle={s.tagline} image={s.image} />

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>Overview</Overline>
            <h2 className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-5 leading-tight">
              {s.tagline}.
            </h2>
            <p className="mt-6 text-[#5C574F] text-lg leading-relaxed">{s.short}</p>
            <Cta to="/book-consultation" className="mt-8" data-testid="detail-book-cta">
              Book Free Consultation
            </Cta>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.1}>
            <div className="grid sm:grid-cols-2 gap-px bg-black/10 border border-black/10 rounded-2xl overflow-hidden">
              {s.features.map((f) => (
                <div key={f} className="bg-white p-6 flex items-start gap-3">
                  <Check className="h-5 w-5 text-[#1C1A17] mt-0.5 shrink-0" />
                  <span className="text-sm font-medium">{f}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-24">
        <Reveal>
          <Overline>Gallery</Overline>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          {s.gallery.map((g, i) => (
            <Reveal key={i} delay={i * 0.08} className={i === 0 ? "md:col-span-2 md:row-span-2" : ""}>
              <div className="overflow-hidden rounded-2xl border border-black/10 h-full group">
                <img
                  src={g}
                  alt={`${s.title} ${i + 1}`}
                  className={`w-full object-cover transition-transform duration-[900ms] group-hover:scale-105 ${
                    i === 0 ? "h-full min-h-[300px]" : "aspect-[4/3]"
                  }`}
                />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {slug === "modular-kitchens" && (
        <section className="bg-[#F5F1EA] border-y border-[#1C1A17]/15 py-24 lg:py-32">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <Reveal>
              <Overline>Kitchen Types</Overline>
              <h2 className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-5 max-w-2xl">
                Find the layout that suits your home.
              </h2>
              <p className="mt-5 max-w-xl text-[#5C574F] text-lg">
                Every home has a different footprint and rhythm. Here are the six modular kitchen layouts we design most often — pick a starting point and we'll refine it together.
              </p>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="kitchen-types">
              {KITCHEN_TYPES.map((k, i) => (
                <Reveal key={k.slug} delay={(i % 3) * 0.08}>
                  <article
                    data-testid={`kitchen-type-${k.slug}`}
                    className="group h-full flex flex-col overflow-hidden rounded-2xl bg-white border border-black/10 hover:border-[#1C1A17]/40 transition-colors duration-500"
                  >
                    <div className="relative overflow-hidden aspect-[4/3]">
                      <img
                        src={k.image}
                        alt={k.title}
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>
                    <div className="flex-1 flex flex-col p-6">
                      <div className="font-head text-xs text-[#1C1A17] tracking-[0.28em] uppercase font-bold">
                        {String(i + 1).padStart(2, "0")} · Layout
                      </div>
                      <h3 className="mt-3 font-head text-2xl font-medium tracking-tight">{k.title}</h3>
                      <p className="mt-3 text-sm text-[#5C574F] leading-relaxed">{k.desc}</p>
                      <ul className="mt-5 space-y-2">
                        {k.bullets.map((b) => (
                          <li key={b} className="flex items-center gap-2 text-sm text-[#0A0A0A]">
                            <Check className="h-4 w-4 text-[#1C1A17]" /> {b}
                          </li>
                        ))}
                      </ul>
                      <div className="mt-6 pt-5 border-t border-black/5 flex items-baseline justify-between gap-3">
                        <div className="text-xs text-[#5C574F]">
                          <span className="font-semibold text-[#0A0A0A]">Best for:</span> {k.bestFor}
                        </div>
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 rounded-2xl bg-white border border-black/10 p-8">
                <p className="text-[#5C574F] max-w-xl">
                  Not sure which layout works for your kitchen? Share your floor plan on your free consultation and our designer will recommend the best fit.
                </p>
                <Cta to="/book-consultation" data-testid="kitchen-types-book">Book Free Consultation</Cta>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <ProcessSection />
      <Testimonials />

      <section className="bg-[#F5F2ED] border-y border-black/5 py-24 lg:py-32">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          <Reveal>
            <Overline>Get started</Overline>
            <h2 className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-5">
              Request a quote for your {s.title.toLowerCase()}.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <LeadForm source="quote" ctaLabel="Get Instant Quote" />
          </Reveal>
        </div>
      </section>

      <FaqSection />
      <CtaBand />
    </>
  );
}
